import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import AccessCodeStep from './AccessCodeStep';
import UserProfileStep from './UserProfileStep';
import { actions as accountActions } from '../../../actions/accountActions';
import { actions as userAccessRequestActions } from '../../../actions/userAccessRequestActions';
import './UserAccessRequestWizard.scss';
import * as types from '../../../constants/actionTypes';
import Routes from '../../../constants/routes';
import Notification from '../../Common/Notification';
import LoadingIndicator from '../../Common/LoadingIndicator/LoadingIndicator';
import MomentTz from 'moment-timezone';
import AlreadyExistsStep from './AlreadyExistsStep';
import LoginStyleWrapper from '../../Common/LoginStyleWrapper/LoginStyleWrapper';
import { isValidEmailAddress } from '../../../helpers/validation';

const Steps = {
    AccessCode: 'ACCESS_CODE',
    UserProfile: 'USER_PROFILE',
    AlreadyExists: 'ALREADY_EXISTS'
};

export class UserAccessRequestWizard extends React.Component {
    constructor(props, context) {
        super(props, context);

        const userRequest = {
            accessCode: '',
            subscriptionId: 0,
            isSubscriptionSSOEnabled: false,
            firstName: '',
            lastName: '',
            email: '',
            userName: '',
            jobTitle: '',
            employeeId: '',
            workPhone: '',
            countryId: '',
            managementCompanyId: '',
            communityIds: '',
            departmentIds: '',
            locationId: '',
            additionalNotes: '',
            timezoneOffset: 0
        }
        
        this.state = {
            isLoading: false,
            isAccessCodeFormValid: true,
            isUserProfileFormValid: true,
            hasEmailBeenModified: false,
            showFullUserProfileForm: false,
            currentStep: Steps.AccessCode,
            blankUserRequest: userRequest,
            userRequest: userRequest,
            communities: [],
            countries: [],
            departments: [],
            managementCompanies: [],
            allLocations: [],
            availableLocations: [],
            hoursOfTheDay: [],
            daysOfTheWeek: []
        };
    }

    onFormChange = (name, value) => {
        this.setState({
            userRequest: {
                ...this.state.userRequest,
                [name]: value
            }
        });
    }

    onManagementCompanyChange = (name, value) => {
        const locations = this.state.allLocations.filter(x => x.managementCompanyId.toString() === value);
        this.setState({
            availableLocations: locations,
            userRequest: {
                ...this.state.userRequest,
                managementCompanyId: value,
            }
        })
    }

    onEmailChange = (email) => {
        /**
         * If userName does not have any value, then automatically put whatever
         * is being typed in email into the userName field as well, to default
         * it being the same as email.  If userName DOES have a value, then only
         * update the email field value.
         */
        if (!this.state.hasEmailBeenModified) {
            this.setState({
                userRequest: {
                    ...this.state.userRequest,
                    email: email,
                    userName: email
                }
            });
        } else {
            this.setState({ userRequest: { ...this.state.userRequest, email: email } });
        }
    }

    onEmailBlur = () => {
        this.setState({ hasEmailBeenModified: true });
    }

    validateAccessCodeForm = () => {
        return this.state.userRequest.accessCode && this.state.userRequest.accessCode.trim().length > 0;
    }

    onAccessCodeStepContinue = async () => {
        try {
            if (!this.validateAccessCodeForm()) {
                this.setState({ isAccessCodeFormValid: false });
                return;
            }

            this.setState({ isLoading: true });
            const response = await this.props.userAccessRequestActions.checkUserRequestAccessCode(this.state.userRequest.accessCode);
            if (response.type === types.CHECK_ACCESS_CODE_SUCCESS) {
                this.setState({
                    userRequest: {
                        ...this.state.userRequest,
                        subscriptionId: response.payload.subscriptionId,
                        isSubscriptionSSOEnabled: response.payload.isSSOEnabled
                    },
                    currentStep: Steps.UserProfile
                });
                await this.loadFilters(response.payload.subscriptionId);
            } else if (response.type === types.CHECK_ACCESS_CODE_FAILURE) {
                if(response.payload.status === 429){
                    Notification("error", "Error", "Too many requests. Try again in a few seconds.")
                } else {
                    Notification("error", "Error", response.payload.message);
                }
            }
        } catch (error) {
            Notification("error", "Error", error);
        } finally {
            this.setState({ isLoading: false });
        }
    }

    loadFilters = async (subId) => {
        try {
            this.setState({ isLoading: true });
            var response = await this.props.userAccessRequestActions.loadUserAccessRequestProfileFilters(subId);
            if (response.type === types.LOAD_USER_REQUEST_PROFILE_FILTERS_SUCCESS) {
                this.setState({
                    communities: response.payload.communities,
                    countries: response.payload.countries,
                    departments: response.payload.departments,
                    managementCompanies: response.payload.managementCompanies,
                    allLocations: response.payload.locations,
                    availableLocations: [], //initially we dont want to expose any locations until the user has selected a mgmt comp
                    hoursOfTheDay: response.payload.hoursOfDay,
                    daysOfTheWeek: response.payload.daysOfWeek
                });
            } else if (response.type === types.LOAD_USER_REQUEST_PROFILE_FILTERS_FAILURE) {
                Notification("error", "Error", response.payload.message);
            }
        } catch (error) {
            Notification("error", "Error", error);
        } finally {
            this.setState({ isLoading: false });
        }
    }

    validateUserProfileBeginForm = () => {
        const {
            firstName,
            lastName,
            email,
            userName
        } = this.state.userRequest;

        if (!(email && email.trim().length > 0 && isValidEmailAddress(email))) {
            Notification("error", 'Email address is not valid', '');
            return false;
        }

        return (
            firstName && firstName.trim().length > 0
            && lastName && lastName.trim().length > 0
            && userName && userName.trim().length > 0
        );
    }

    validateUserProfileFullForm = () => {
        const {
            firstName,
            lastName,
            email,
            userName,
            jobTitle,
            employeeId,
            managementCompanyId,
            countryId,
            communityIds,
            locationId,
            isSubscriptionSSOEnabled
        } = this.state.userRequest;

        if (!(email && email.trim().length > 0 && isValidEmailAddress(email))) {
            Notification("error", 'Email address is not valid', '');
            return false;
        }

        return (
            firstName && firstName.trim().length > 0
            && lastName && lastName.trim().length > 0
            && userName && userName.trim().length > 0
            && jobTitle && jobTitle.trim().length > 0
            && managementCompanyId && managementCompanyId.trim().length > 0
            && countryId && countryId.trim().length > 0
            && communityIds && communityIds.trim().length > 0
            && locationId && locationId.trim().length > 0
            && (!isSubscriptionSSOEnabled || (isSubscriptionSSOEnabled && employeeId && employeeId.trim().length > 0))
        );
    }

    onUserProfileStepContinue = async () => {

        if (!this.validateUserProfileBeginForm()) {
            this.setState({ isUserProfileFormValid: false });
            return;
        }
        try {
            this.setState({ isLoading: true });
            var response = await this.props.userAccessRequestActions.checkUserRequestUserName(this.state.userRequest.subscriptionId, this.state.userRequest.userName.trim(), this.state.userRequest.email);
            if (response.type === types.CHECK_USER_NAME_SUCCESS) {
                const payload = response.payload;

                if (payload.isUserNameTaken && !payload.isSSOUser) {
                    this.setState({ isLoading: false, currentStep: Steps.AlreadyExists });
                    
                } else if (response.payload.isUserNameTaken || response.payload.isSSO) {
                    this.setState({ isLoading: false, isUserProfileFormValid: false });

                } else {
                    // show the full form on the user profile step
                    this.setState({ isLoading: false, currentStep: Steps.UserProfile, showFullUserProfileForm: true });
                }
                
            } else if (response.type === types.CHECK_USER_NAME_FAILURE) {
                Notification("error", "Error", response.payload.message);
                this.setState({ isLoading: false });
            }
        } catch (error) {
            Notification("error", "Error", error);
            this.setState({ isLoading: false });
        }

    }

    getTimezoneOffset = () => {
        const tz = MomentTz.tz.guess();
        return MomentTz.tz(tz)._offset;
    }

    assertNotAllCaps = (value) => {
        if (value && value.trim().length > 0 && value === value.toUpperCase()) {
            const newValue = value.charAt(0) + value.slice(1).toLowerCase();
            return newValue;
        } else {
            return value;
        }
    }

    onSubmitRequest = async () => {
        try {
            if (!this.validateUserProfileFullForm()) {
                this.setState({ isUserProfileFormValid: false });
                return;
            }

            this.setState({ isLoading: true });
            const request = { 
                ...this.state.userRequest,
            };

            if (request.communityIds && request.communityIds.trim().length > 0) {
                request.communityIds = request.communityIds.split(',');
            }

            if (request.departmentIds && request.departmentIds.trim().length > 0) {
                request.departmentIds = request.departmentIds.split(',');
            }

            request.timezoneOffset = this.getTimezoneOffset();
            request.firstName = this.assertNotAllCaps(request.firstName);
            request.lastName = this.assertNotAllCaps(request.lastName);

            const response = await this.props.userAccessRequestActions.createUserAccessRequest(request);
            if (response.type === types.CREATE_USER_ACCESS_REQUEST_SUCCESS) {
                Notification("success", "Success", "User Access Request submitted successfully");
                this.onCancel(); // Take them back to the Sign In screen
            } else if (response.type === types.CREATE_USER_ACCESS_REQUEST_FAILURE) {
                Notification("error", "Error", response.payload.message);
                this.setState({ isLoading: false });
            }
        } catch (error) {
            Notification("error", "Error", error);
            this.setState({ isLoading: false });
        }
    }

    onAlreadyExistsStepContinue = async () => {
        // reset password, when it comes back, send to login page
        try {
            this.setState({ isLoading: true });
            await this.props.accountActions.forgotPassword(this.state.userRequest.userName.trim())
            Notification("success", "", "An email has been sent to you with instructions on how to reset your password.");
            this.setState({ isLoading: false });
        } catch (error) {
            Notification("error", "Error Resetting Password", error);
            this.setState({ isLoading: false });
        }
    }

    onAlreadyExistsStepCancel = () => {
        this.setState({ currentStep: Steps.UserProfile });
    }

    onCancel = () => {
        this.props.history.push(Routes.Root);
    }

    renderStep = () => {
        switch (this.state.currentStep) {
            case Steps.AccessCode: {
                return <AccessCodeStep
                    userRequest={this.state.userRequest}
                    onContinue={this.onAccessCodeStepContinue}
                    onFormChange={this.onFormChange}
                    isFormValid={this.state.isAccessCodeFormValid}
                />
            }
            case Steps.UserProfile: {
                return <UserProfileStep
                    userRequest={this.state.userRequest}
                    onContinue={this.onUserProfileStepContinue}
                    onSubmitRequest={this.onSubmitRequest}
                    onFormChange={this.onFormChange}
                    onEmailChange={this.onEmailChange}
                    onEmailBlur={this.onEmailBlur}
                    isFormValid={this.state.isUserProfileFormValid}
                    userAccessRequestActions={this.props.userAccessRequestActions}
                    onManagementCompanyChange={this.onManagementCompanyChange}
                    communities={this.state.communities}
                    countries={this.state.countries}
                    departments={this.state.departments}
                    managementCompanies={this.state.managementCompanies}
                    locations={this.state.availableLocations}
                    showFullForm={this.state.showFullUserProfileForm}
                />
            }
            case Steps.AlreadyExists: {
                return <AlreadyExistsStep
                    userRequest={this.state.userRequest}
                    onResetPassword={this.onAlreadyExistsStepContinue}
                    onCancel={this.onAlreadyExistsStepCancel}
                />
            }
            default: {
                Notification("error", "Error", "Unknown User Access Request Step");
                return null;
            }
        }
    }

    render() {

        const isWide = (this.state.currentStep === Steps.UserProfile) ? true : false;
        return (
            <LoginStyleWrapper isWide={isWide} showSignUpButton={false}>
                <div className="user-access-request-wizard">
                    <LoadingIndicator isLoading={this.state.isLoading} isFullscreen={false}>
                        {this.renderStep()}
                    </LoadingIndicator>
                </div>                
            </LoginStyleWrapper>
        );
    }
}

UserAccessRequestWizard.propTypes = {
    userAccessRequestActions: PropTypes.object.isRequired,
    accountActions: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired
};

function mapStateToProps(state, ownProps) {
    return {
        history: ownProps.history
    }
}

function mapDispatchToProps(dispatch) {
    return {
        accountActions: bindActionCreators({ ...accountActions }, dispatch),
        userAccessRequestActions: bindActionCreators({ ...userAccessRequestActions }, dispatch)
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(UserAccessRequestWizard);
